import { Link } from "react-router-dom";
import { Storefront, MapPin, Lightning, ShoppingCart, ChartLineUp, Sparkle, Robot, ShieldCheck } from "@phosphor-icons/react";
import SiteFooter from "@/components/SiteFooter";

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Top nav */}
      <nav className="glass sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/QDplatform" className="flex items-center gap-2" data-testid="home-logo-link">
            <div className="w-9 h-9 rounded-xl bg-[#F8CB46] flex items-center justify-center">
              <Lightning weight="fill" size={20} className="text-black" />
            </div>
            <span className="font-heading text-xl font-black tracking-tight">QuickDukaan</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/home" className="hidden sm:inline-flex text-sm font-semibold text-gray-700 hover:text-black px-3 py-2" data-testid="nav-marketplace">Marketplace</Link>
            <Link to="/login" className="text-sm font-semibold text-gray-700 hover:text-black px-3 py-2" data-testid="nav-customer-login">Shop as Customer</Link>
            <Link to="/seller/login" className="qd-btn-primary text-sm" data-testid="nav-start-selling">Sell on QuickDukaan</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 -left-20 w-96 h-96 bg-[#F8CB46]/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#15803D]/15 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16 grid lg:grid-cols-2 gap-10 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest text-gray-700 mb-5">
              <Sparkle weight="fill" size={14} className="text-[#F8CB46]" />
              India's quick commerce OS
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-gray-900">
              Launch your own<br />
              <span className="relative inline-block">
                <span className="relative z-10">Blinkit-like</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#F8CB46] -z-0 rounded-sm" />
              </span>{" "}
              store.
            </h1>
            <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-xl">
              Turn your kirana, grocery or boutique store into a hyperlocal delivery app — with AI product uploads, smart pincode delivery, and Razorpay built-in.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/seller/login" className="qd-btn-primary inline-flex items-center gap-2" data-testid="hero-start-btn">
                <Storefront weight="bold" size={18} /> Sell on QuickDukaan
              </Link>
              <Link to="/home" className="qd-btn-green inline-flex items-center gap-2" data-testid="hero-demo-btn">
                <ShoppingCart weight="bold" size={18} /> Shop as Customer
              </Link>
            </div>
            <div className="mt-8 flex gap-6 text-sm text-gray-500">
              <div><div className="font-heading font-black text-2xl text-gray-900">10 min</div>setup time</div>
              <div><div className="font-heading font-black text-2xl text-gray-900">0%</div>code needed</div>
              <div><div className="font-heading font-black text-2xl text-gray-900">₹0</div>to start</div>
            </div>
          </div>

          <div className="relative">
            <div className="relative mx-auto w-full max-w-md">
              <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#F8CB46] rounded-2xl rotate-6" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#15803D]/20 rounded-3xl -rotate-6" />
              <div className="relative qd-card overflow-hidden p-0">
                <img src="https://images.unsplash.com/photo-1601972602237-8c79241e468b?w=600&q=80" alt="QuickDukaan app" className="w-full h-[420px] object-cover" />
                <div className="absolute bottom-0 inset-x-0 p-4 glass border-t border-gray-100 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Delivering in</div>
                    <div className="font-heading font-black text-2xl text-gray-900">20 minutes</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">to</div>
                    <div className="font-semibold text-gray-900">110034 Pitampura</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="mb-10">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Built for Bharat</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Everything your kirana needs.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: Robot, title: "AI product uploads", text: "Click a photo. AI fills name, category, description, tags." },
            { icon: MapPin, title: "Pincode-wise ETA", text: "Set 20-min for Pitampura, 45-min for Ashok Vihar." },
            { icon: Lightning, title: "Self + Borzo/Porter", text: "Pick delivery per order. Save money on small drops." },
            { icon: ChartLineUp, title: "Realtime dashboard", text: "Orders, revenue, top products, repeat customers." },
            { icon: ShieldCheck, title: "OTP-only checkout", text: "No passwords. Customers love the speed." },
            { icon: Sparkle, title: "Plans from ₹0", text: "Start free. Upgrade only when you scale past 100 SKUs." },
          ].map((f) => (
            <div key={f.title} className="qd-card p-5">
              <div className="w-10 h-10 rounded-xl bg-[#F8CB46]/20 flex items-center justify-center mb-4">
                <f.icon weight="duotone" size={22} className="text-[#15803D]" />
              </div>
              <div className="font-heading font-bold text-lg mb-1">{f.title}</div>
              <div className="text-sm text-gray-600 leading-relaxed">{f.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Two-option CTA */}
      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#F8CB46]">I want to</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-3">Pick your path.</h2>
            <p className="text-gray-300 mt-3 max-w-md">
              QuickDukaan is built for two crowds — speed-loving shoppers and ambitious shopkeepers.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Link to="/login" className="block bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition" data-testid="cta-customer">
              <div className="w-10 h-10 rounded-xl bg-[#15803D] flex items-center justify-center mb-3">
                <ShoppingCart weight="fill" size={20} />
              </div>
              <div className="font-heading font-black text-lg">Shop as Customer</div>
              <div className="text-xs text-gray-400 mt-1">Browse stores · Track delivery in 20 min</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#F8CB46] mt-3">Continue →</div>
            </Link>
            <Link to="/seller/login" className="block bg-[#F8CB46] text-black rounded-2xl p-5 hover:brightness-95 transition" data-testid="cta-seller">
              <div className="w-10 h-10 rounded-xl bg-black/10 flex items-center justify-center mb-3">
                <Storefront weight="fill" size={20} />
              </div>
              <div className="font-heading font-black text-lg">Sell on QuickDukaan</div>
              <div className="text-xs text-black/70 mt-1">Launch your store · ₹0 to start</div>
              <div className="text-[10px] uppercase tracking-widest font-bold mt-3">Open seller portal →</div>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
